import { FrontendApi, Configuration, UpdateLoginFlowBody } from '@ory/client';
import { z } from 'zod';

// ============================================================================
// Zod Schemas for Kratos Types
// ============================================================================

// Verifiable Address Schema
export const VerifiableAddressSchema = z.object({
  id: z.string().uuid(),
  value: z.string().email(),
  verified: z.boolean(),
  via: z.enum(['email', 'sms']),
  status: z.string(),
  verified_at: z.string().datetime().optional().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// Recovery Address Schema
export const RecoveryAddressSchema = z.object({
  id: z.string().uuid(),
  value: z.string().email(),
  via: z.enum(['email', 'sms']),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// Identity Traits Schema (flexible for custom traits)
export const IdentityTraitsSchema = z.object({
  email: z.string().email(),
  name: z.string().optional(),
}).catchall(z.unknown()); // Allow additional custom traits

// Project Schema for tenant metadata
export const ProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: z.string(),
});

// Tenant Schema for metadata_public
export const TenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: z.string(),
  projects: z.array(ProjectSchema).optional(),
});

// Metadata Public Schema
export const MetadataPublicSchema = z.object({
  tenants: z.array(TenantSchema).optional(),
}).catchall(z.unknown()); // Allow additional metadata

// Identity Schema
export const IdentitySchema = z.object({
  id: z.string().uuid(),
  schema_id: z.string(),
  schema_url: z.string().url(),
  state: z.enum(['active', 'inactive']),
  state_changed_at: z.string().datetime().optional().nullable(),
  traits: IdentityTraitsSchema,
  verifiable_addresses: z.array(VerifiableAddressSchema).optional().nullable(),
  recovery_addresses: z.array(RecoveryAddressSchema).optional().nullable(),
  metadata_public: MetadataPublicSchema.optional().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// Authentication Method Schema
export const AuthenticationMethodSchema = z.object({
  method: z.enum(['password', 'oidc', 'webauthn', 'totp', 'lookup_secret', 'link', 'code']),
  aal: z.enum(['aal1', 'aal2']).optional(),
  completed_at: z.string().datetime().optional(),
});

// Device Schema
export const DeviceSchema = z.object({
  id: z.string().uuid(),
  ip_address: z.string().optional().nullable(),
  user_agent: z.string().optional().nullable(),
  location: z.string().optional().nullable(),
});

// Session Schema
export const SessionSchema = z.object({
  id: z.string().uuid(),
  active: z.boolean(),
  expires_at: z.string().datetime(),
  authenticated_at: z.string().datetime(),
  authenticator_assurance_level: z.enum(['aal0', 'aal1', 'aal2']),
  authentication_methods: z.array(AuthenticationMethodSchema).optional().nullable(),
  issued_at: z.string().datetime(),
  identity: IdentitySchema,
  devices: z.array(DeviceSchema).optional().nullable(),
});

// UI Node Attributes (extended to support all Kratos node types)
export const UiNodeAttributesSchema = z.object({
  name: z.string().optional(), // Make name optional as some nodes don't have it
  type: z.string(),
  value: z.unknown().optional(),
  required: z.boolean().optional(),
  disabled: z.boolean().optional(),
  node_type: z.string().optional(),
  // Script-specific attributes
  src: z.string().optional(),
  async: z.boolean().optional(),
  referrerpolicy: z.string().optional(),
  crossorigin: z.string().optional(),
  integrity: z.string().optional(),
  id: z.string().optional(),
  nonce: z.string().optional(),
  // Button/input-specific attributes
  onclick: z.string().optional(),
  onclickTrigger: z.string().optional(),
  onload: z.string().optional(),
  onloadTrigger: z.string().optional(),
}).catchall(z.unknown()); // Allow additional attributes

// UI Node Schema
export const UiNodeSchema = z.object({
  type: z.string(),
  group: z.string(),
  attributes: UiNodeAttributesSchema,
  messages: z.array(z.object({
    id: z.number(),
    text: z.string(),
    type: z.enum(['info', 'error', 'success']),
    context: z.record(z.unknown()).optional(),
  })).optional().nullable(),
  meta: z.record(z.unknown()).optional(),
});

// UI Container Schema
export const UiContainerSchema = z.object({
  action: z.string().url(),
  method: z.string(),
  nodes: z.array(UiNodeSchema),
  messages: z.array(z.object({
    id: z.number(),
    text: z.string(),
    type: z.enum(['info', 'error', 'success']),
    context: z.record(z.unknown()).optional(),
  })).optional().nullable(),
});

// Login Flow Schema
export const LoginFlowSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(['browser', 'api']),
  expires_at: z.string().datetime(),
  issued_at: z.string().datetime(),
  request_url: z.string().url(),
  ui: UiContainerSchema,
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
  refresh: z.boolean().optional(),
  requested_aal: z.enum(['aal1', 'aal2']).optional(),
  session_token_exchange_code: z.string().optional(),
  state: z.unknown().optional(),
});

// Registration Flow Schema
export const RegistrationFlowSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(['browser', 'api']),
  expires_at: z.string().datetime(),
  issued_at: z.string().datetime(),
  request_url: z.string().url(),
  ui: UiContainerSchema,
  oauth2_login_challenge: z.string().optional(),
  oauth2_login_request: z.unknown().optional(),
  organization_id: z.string().uuid().optional().nullable(),
  return_to: z.string().optional(),
  session_token_exchange_code: z.string().optional(),
  state: z.unknown().optional(),
  transient_payload: z.record(z.unknown()).optional(),
});

// Recovery Flow Schema
export const RecoveryFlowSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(['browser', 'api']),
  expires_at: z.string().datetime(),
  issued_at: z.string().datetime(),
  request_url: z.string().url(),
  ui: UiContainerSchema,
  state: z.unknown(),
  return_to: z.string().optional(),
});

// Verification Flow Schema
export const VerificationFlowSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(['browser', 'api']),
  expires_at: z.string().datetime(),
  issued_at: z.string().datetime(),
  request_url: z.string().url(),
  ui: UiContainerSchema,
  state: z.unknown(),
  return_to: z.string().optional(),
});

// Logout Flow Schema
export const LogoutFlowSchema = z.object({
  logout_token: z.string(),
  logout_url: z.string().url(),
});

// ============================================================================
// TypeScript Types (inferred from Zod schemas)
// ============================================================================

export type VerifiableAddress = z.infer<typeof VerifiableAddressSchema>;
export type RecoveryAddress = z.infer<typeof RecoveryAddressSchema>;
export type IdentityTraits = z.infer<typeof IdentityTraitsSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type Tenant = z.infer<typeof TenantSchema>;
export type MetadataPublic = z.infer<typeof MetadataPublicSchema>;
export type Identity = z.infer<typeof IdentitySchema>;
export type AuthenticationMethod = z.infer<typeof AuthenticationMethodSchema>;
export type Device = z.infer<typeof DeviceSchema>;
export type Session = z.infer<typeof SessionSchema>;
export type UiNodeAttributes = z.infer<typeof UiNodeAttributesSchema>;
export type UiNode = z.infer<typeof UiNodeSchema>;
export type UiContainer = z.infer<typeof UiContainerSchema>;
export type LoginFlow = z.infer<typeof LoginFlowSchema>;
export type RegistrationFlow = z.infer<typeof RegistrationFlowSchema>;
export type RecoveryFlow = z.infer<typeof RecoveryFlowSchema>;
export type VerificationFlow = z.infer<typeof VerificationFlowSchema>;
export type LogoutFlow = z.infer<typeof LogoutFlowSchema>;

// ============================================================================
// Kratos Client Configuration
// ============================================================================

// Note: All API calls now use kratosApi (@ory/client) instead of direct axios calls

/**
 * Ory Kratos FrontendApi client - matches original Nuxt2 implementation exactly
 */
const basePath = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
const identityConfiguration = new Configuration({
  basePath: basePath,
  baseOptions: {
    withCredentials: true,
    timeout: 5000,
  },
});

export const kratosApi = new FrontendApi(identityConfiguration);

// ============================================================================
// Kratos API Functions
// ============================================================================

/**
 * Get the current session (whoami endpoint) - matches original Nuxt2 implementation
 * @returns Promise<Session> - The current active session
 * @throws Error if not authenticated or session is invalid
 */
export async function toSession(): Promise<Session> {
  const response = await kratosApi.toSession();
  return SessionSchema.parse(response.data);
}

/**
 * Create a new login flow - matches original Nuxt2 implementation
 * @param refresh - Whether to refresh the session
 * @param aal - Authenticator Assurance Level
 * @param returnTo - URL to return to after login
 * @returns Promise<LoginFlow> - The login flow
 */
export async function createLoginFlow(params?: {
  refresh?: boolean;
  aal?: 'aal1' | 'aal2';
  returnTo?: string;
}): Promise<LoginFlow> {
  const response = await kratosApi.createBrowserLoginFlow(params);
  return LoginFlowSchema.parse(response.data);
}

/**
 * Get an existing login flow by ID - matches original Nuxt2 implementation
 * @param id - The flow ID
 * @returns Promise<LoginFlow> - The login flow
 */
export async function getLoginFlow(id: string): Promise<LoginFlow> {
  const response = await kratosApi.getLoginFlow({ id });
  return LoginFlowSchema.parse(response.data);
}

/**
 * Update a login flow (submit login credentials) - matches original Nuxt2 implementation
 * @param flow - The login flow object
 * @param body - The login form data
 * @returns Promise<{ session?: Session; flow?: LoginFlow }> - The response
 */
export async function updateLoginFlow(
  flow: LoginFlow,
  body: {
    method: string;
    identifier?: string;
    password?: string;
    csrf_token?: string;
    [key: string]: unknown;
  }
): Promise<{ session?: Session; flow?: LoginFlow }> {
  console.log('Updating login flow with @ory/client:', {
    flowId: flow.id,
    method: body.method,
    identifier: body.identifier,
    csrf_token: body.csrf_token,
  });
  
  console.log('Full request body:', body);
  console.log('Flow action URL:', flow.ui.action);
  
  try {
    // Use @ory/client FrontendApi exactly like the original Nuxt2 implementation
    console.log('Calling kratosApi.updateLoginFlow...');
    const response = await kratosApi.updateLoginFlow({
      flow: flow.id,
      updateLoginFlowBody: body as UpdateLoginFlowBody,
    });
    
    console.log('Login flow response success:', response.data);
    
    // Response can contain either a session (success) or an updated flow (needs more input)
    if (response.data.session) {
      return {
        session: SessionSchema.parse(response.data.session),
      };
    }
    
    // For login flows, the response is always a SuccessfulNativeLogin which contains session
    // If no session, it means we need to handle the flow differently
    return {
      session: response.data.session ? SessionSchema.parse(response.data.session) : undefined,
    };
  } catch (error: any) {
    console.error('Login flow update error:', error);
    console.error('Error response status:', error.response?.status);
    console.error('Error response data:', error.response?.data);
    
    // Handle Kratos error responses exactly like the original Nuxt2 implementation
    // In Nuxt2: error.response.data is ALWAYS used as the new flow
    if (error.response?.data) {
      const errorData = error.response.data;
      console.log('HTTP Status:', error.response.status);
      console.log('Error Flow Body:', errorData);
      
      // Check for specific error codes that require redirect (like original)
      if ([403, 404, 410].includes(errorData.error?.code)) {
        console.log('Critical error, should redirect to create new flow');
        throw error; // Let the component handle redirect
      }
      
      // For identifier_first with 400 error, this is EXPECTED behavior
      // The error response contains the updated flow with available methods
      if (error.response.status === 400 && errorData.id) {
        console.log('400 error with flow - this is NORMAL for identifier_first');
        const flowResult = {
          flow: LoginFlowSchema.parse(errorData),
        };
        console.log('Returning flow result:', flowResult);
        return flowResult;
      }
      
      // For other errors, also try to use the flow data if available
      if (errorData.id) {
        console.log('Using error response data as new flow');
        return {
          flow: LoginFlowSchema.parse(errorData),
        };
      }
    }
    
    throw error;
  }
}

/**
 * Create a new registration flow
 * @param returnTo - URL to return to after registration
 * @returns Promise<RegistrationFlow> - The registration flow
 */
export async function createRegistrationFlow(params?: {
  returnTo?: string;
}): Promise<RegistrationFlow> {
  const response = await kratosApi.createBrowserRegistrationFlow(params);
  return RegistrationFlowSchema.parse(response.data);
}

/**
 * Get an existing registration flow by ID
 * @param id - The flow ID
 * @returns Promise<RegistrationFlow> - The registration flow
 */
export async function getRegistrationFlow(id: string): Promise<RegistrationFlow> {
  const response = await kratosApi.getRegistrationFlow({ id });
  return RegistrationFlowSchema.parse(response.data);
}

/**
 * Update a registration flow (submit registration data)
 * @param flowId - The flow ID
 * @param body - The registration form data
 * @returns Promise<{ session?: Session; flow?: RegistrationFlow }> - The response
 */
export async function updateRegistrationFlow(
  flowId: string,
  body: {
    method: string;
    traits: IdentityTraits;
    password?: string;
    csrf_token?: string;
    [key: string]: unknown;
  }
): Promise<{ session?: Session; flow?: RegistrationFlow }> {
  try {
    const response = await kratosApi.updateRegistrationFlow({
      flow: flowId,
      updateRegistrationFlowBody: body as any,
    });
    
    if (response.data.session) {
      return {
        session: SessionSchema.parse(response.data.session),
      };
    }
    
    // If no session, return empty object (registration flow continues)
    return {};
  } catch (error: any) {
    // Handle Kratos error responses
    if (error.response?.data?.id) {
      return {
        flow: RegistrationFlowSchema.parse(error.response.data),
      };
    }
    throw error;
  }
}

/**
 * Create a new recovery flow
 * @param returnTo - URL to return to after recovery
 * @returns Promise<RecoveryFlow> - The recovery flow
 */
export async function createRecoveryFlow(params?: {
  returnTo?: string;
}): Promise<RecoveryFlow> {
  const response = await kratosApi.createBrowserRecoveryFlow(params);
  return RecoveryFlowSchema.parse(response.data);
}

/**
 * Get an existing recovery flow by ID
 * @param id - The flow ID
 * @returns Promise<RecoveryFlow> - The recovery flow
 */
export async function getRecoveryFlow(id: string): Promise<RecoveryFlow> {
  const response = await kratosApi.getRecoveryFlow({ id });
  return RecoveryFlowSchema.parse(response.data);
}

/**
 * Create a new verification flow
 * @param returnTo - URL to return to after verification
 * @returns Promise<VerificationFlow> - The verification flow
 */
export async function createVerificationFlow(params?: {
  returnTo?: string;
}): Promise<VerificationFlow> {
  const response = await kratosApi.createBrowserVerificationFlow(params);
  return VerificationFlowSchema.parse(response.data);
}

/**
 * Get an existing verification flow by ID
 * @param id - The flow ID
 * @returns Promise<VerificationFlow> - The verification flow
 */
export async function getVerificationFlow(id: string): Promise<VerificationFlow> {
  const response = await kratosApi.getVerificationFlow({ id });
  return VerificationFlowSchema.parse(response.data);
}

/**
 * Create a new settings flow
 * @param returnTo - URL to return to after settings
 * @returns Promise<SettingsFlow> - The settings flow
 */
export async function createSettingsFlow(params?: {
  returnTo?: string;
}): Promise<any> {
  const response = await kratosApi.createBrowserSettingsFlow(params);
  return response.data;
}

/**
 * Get an existing settings flow by ID
 * @param id - The flow ID
 * @returns Promise<SettingsFlow> - The settings flow
 */
export async function getSettingsFlow(id: string): Promise<any> {
  const response = await kratosApi.getSettingsFlow({ id });
  return response.data;
}

/**
 * Create a logout flow
 * @returns Promise<LogoutFlow> - The logout flow with logout URL
 */
export async function createLogoutFlow(): Promise<LogoutFlow> {
  const response = await kratosApi.createBrowserLogoutFlow();
  return LogoutFlowSchema.parse(response.data);
}

/**
 * Perform logout by calling the logout URL
 * @param logoutToken - The logout token from createLogoutFlow
 */
export async function performLogout(logoutToken: string): Promise<void> {
  await kratosApi.updateLogoutFlow({ token: logoutToken });
}

// ============================================================================
// Session Validation Helper
// ============================================================================

/**
 * Validates if a session is currently active
 * @param session - The session to validate
 * @returns boolean - True if session is active and not expired
 */
export function isSessionValid(session: Session | null | undefined): boolean {
  if (!session) {
    return false;
  }
  
  if (!session.active) {
    return false;
  }
  
  const expiresAt = new Date(session.expires_at);
  const now = new Date();
  
  return expiresAt > now;
}

/**
 * Checks if the current user is authenticated by validating the session
 * @returns Promise<boolean> - True if authenticated with valid session
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const session = await toSession();
    return isSessionValid(session);
  } catch (error) {
    return false;
  }
}

/**
 * Gets the current user's identity if authenticated
 * @returns Promise<Identity | null> - The user's identity or null
 */
export async function getCurrentIdentity(): Promise<Identity | null> {
  try {
    const session = await toSession();
    if (isSessionValid(session)) {
      return session.identity;
    }
    return null;
  } catch (error) {
    return null;
  }
}
